import { WSList } from "@wingspanhq/fe-component-library";
import {
  SharedFileRequestContextType,
  SharedFileRequestJobType,
  SharedFileRequestStatus
} from "@wingspanhq/wingspan-api-sdk";
import { useEffect } from "react";

import { getRequirementDefinitionIsWayfinder } from "../../../../../../modules/RequirementDefinitions/utils";
import { WSQueries } from "../../../../../../query/WSQuery";
import { WayfinderWidget } from "../../../../../../shared/components/WayfinderWidget";
import { queriesSharedFileRequests } from "../../../../../../v3/query/queriesSharedFileRequests";
import { AIValidationWidget } from "../../../../components/AIValidationWidget";
import { selectorIsRequirementReadyToDownloadFile } from "../../../../selectors/selectorIsRequirementReadyToDownloadFile";
import {
  RequirementContextType,
  RequirementTableCallback,
  RequirementTableItemData
} from "../../../../types";
import { SHARED_FILE_REQUIREMENT_EVENT } from "../../configs/utils";
import { AssurelyWidget } from "../AssurelyWidget";

type Props = {
  sharedFileRequestId: string;
  contextType: RequirementContextType;
  onSuccess: RequirementTableCallback;
  requirement: RequirementTableItemData;
};

export const SharedFileDetailsWidgetsContainer: React.FC<Props> = ({
  sharedFileRequestId,
  contextType,
  requirement,
  onSuccess
}) => {
  const querySharedFilesRequest =
    queriesSharedFileRequests.getSharedFileRequest.useQuery({
      sharedFileRequestId
    });
  const sharedFileRequestData = querySharedFilesRequest.data;
  const isWayfinder = getRequirementDefinitionIsWayfinder({
    type: requirement.type,
    contextType: sharedFileRequestData?.contextType
  });

  useEffect(() => {
    // Refetch the shared file request if the status is Processing
    if (sharedFileRequestData?.status === SharedFileRequestStatus.PROCESSING) {
      // Refetch the shared file request for every 5 seconds
      const interval = setInterval(() => {
        // This is to ensure that the status is updated real-time in the UI
        querySharedFilesRequest.refetch().then(result => {
          if (result.data?.status === SharedFileRequestStatus.COMPLETE) {
            clearInterval(interval);
            onSuccess(
              SHARED_FILE_REQUIREMENT_EVENT.ValidationSuccess,
              requirement
            );
          }
        });
      }, 3000);
      return () => clearInterval(interval);
    }
    // re-arm only on status/query change — onSuccess/requirement are new
    // objects every render and would reset the polling interval
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedFileRequestData?.status, querySharedFilesRequest]);

  return (
    <WSQueries queries={{ querySharedFilesRequest }}>
      {({ querySharedFilesRequestData: sharedFileRequest }) => {
        // Do not show any widgets for DocumentUpload requirement type if contextType is None
        if (
          sharedFileRequest.contextType === SharedFileRequestContextType.NONE
        ) {
          return null;
        }

        // Filter out validation jobs from the shared file request
        const validationJobs = (sharedFileRequest.jobs ?? []).filter(
          job => job.type === SharedFileRequestJobType.VALIDATION
        );
        return (
          <WSList gap="L">
            {/* Show AssurelyWidget widget only for payees */}
            {!isWayfinder &&
            contextType === RequirementContextType.Payer &&
            !selectorIsRequirementReadyToDownloadFile(requirement) ? (
              <AssurelyWidget />
            ) : null}
            <AIValidationWidget
              contextType={contextType}
              validationItems={validationJobs}
            />
            {/* Show WayFinder widget only for payers */}
            {contextType === RequirementContextType.Payee && (
              <WayfinderWidget />
            )}
          </WSList>
        );
      }}
    </WSQueries>
  );
};
